import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import '@humanbase/dalliance';

import { updateGenomicRange } from '../../../actions/GenomeActions';
import { CHROMOSOMES, AWS_URL } from '../../../settings';

const COORD_HG19 = {
  speciesName: 'Human',
  taxon: 9606,
  auth: 'GRCh',
  version: '37',
  ucscName: 'hg19',
};

const COORD_HG38 = {
  speciesName: 'Human',
  taxon: 9606,
  auth: 'GRCh',
  version: '38',
  ucscName: 'hg38',
};

const GENOME_HG19 = {
  name: 'Genome',
  twoBitURI: `${AWS_URL}/tracks/hg19.2bit`,
  tier_type: 'sequence',
  pinned: true,
};

const GENOME_HG38 = {
  name: 'Genome',
  twoBitURI: `${AWS_URL}/tracks/GRCh38/hg38.2bit`,
  tier_type: 'sequence',
  pinned: true,
};

const GENCODE19 = {
  name: 'GENCODE 19',
  desc: 'Gene structures from GENCODE 19',
  bwgURI: `${AWS_URL}/tracks/gencode.bb`,
  stylesheet_uri: `${AWS_URL}/tracks/gencode.xml`,
  collapseSuperGroups: true,
  trixURI: `${AWS_URL}/tracks/geneIndex.ix`,
};

const GENCODE21 = {
  name: 'GENCODE 21',
  desc: 'Gene structures from GENCODE 21',
  bwgURI: `${AWS_URL}/tracks/GRCh38/gencode.v21.annotation.bb`,
  stylesheet_uri: `${AWS_URL}/tracks/GRCh38/gencode2.xml`,
  collapseSuperGroups: true,
  trixURI: `${AWS_URL}/tracks/GRCh38/gencode.v21.annotation.ix`,
};

const CHROM_HMM = {
  name: 'GM12878 ChromHMM',
  desc: 'GM12878 ChromHMM Genome Segmentation',
  pennant: 'https://genome.ucsc.edu/images/encodeThumbnail.jpg',
  disabled: true,
  bwgURI: `${AWS_URL}/tracks/gm12878.ChromHMM.bb`,
  style: [
    {
      type: 'bigbed',
      style: {
        glyph: 'BOX',
        FGCOLOR: 'black',
        BGCOLOR: 'blue',
        HEIGHT: 8,
        BUMP: false,
        LABEL: false,
        ZINDEX: 20,
        BGITEM: true,
        id: 'style1',
      },
    },
    {
      type: 'bb-translation',
      style: {
        glyph: 'BOX',
        FGCOLOR: 'black',
        BGITEM: true,
        BGCOLOR: 'red',
        HEIGHT: 10,
        BUMP: true,
        ZINDEX: 20,
        id: 'style2',
      },
    },
    {
      type: 'bb-transcript',
      style: {
        glyph: 'BOX',
        FGCOLOR: 'black',
        BGCOLOR: 'white',
        HEIGHT: 10,
        ZINDEX: 10,
        BUMP: true,
        LABEL: true,
        id: 'style3',
      },
    },
  ],
};

const CONSERVATION = {
  name: 'Conservation',
  desc: 'Conservation',
  bwgURI: `${AWS_URL}/tracks/phastCons46way.bw`,
  noDownsample: true,
  disabled: true,
};

const SOURCES_HG19 = [GENOME_HG19, GENCODE19, CONSERVATION, CHROM_HMM];
const SOURCES_HG38 = [
  GENOME_HG38,
  GENCODE21,
  { ...GENCODE19, mapping: 'hg19ToHg38', disabled: true },
];

const GENOME_CONFIG = {
  hg19: {
    sources: SOURCES_HG19,
    coordSystem: COORD_HG19,
  },
  hg38: {
    sources: SOURCES_HG38,
    coordSystem: COORD_HG38,
  },
};

class GenomeBrowser extends PureComponent {
  static propTypes = {
    chromosome: PropTypes.string,
    startPosition: PropTypes.number,
    endPosition: PropTypes.number,
    onFeatureInfo: PropTypes.func,
    dispatch: PropTypes.func.isRequired,
    sources: PropTypes.arrayOf(PropTypes.object),
    gene: PropTypes.string,
    genome: PropTypes.string,
    onGeneSearch: PropTypes.func,
    thresholds: PropTypes.objectOf(PropTypes.number),
    onTrackSelect: PropTypes.func,
  };

  static defaultProps = {
    sources: [],
    chromosome: 'chr1',
    startPosition: 700000,
    endPosition: 1000000,
    onFeatureInfo: null,
    gene: null,
    onGeneSearch: null,
    thresholds: {},
    onTrackSelect: null,
    genome: 'hg19',
  };

  componentDidMount() {
    this.createPlot();
  }

  componentDidUpdate(prevProps) {
    const { chromosome, startPosition, endPosition, thresholds } = this.props;
    if (
      prevProps.chromosome !== chromosome ||
      prevProps.startPosition !== startPosition ||
      prevProps.endPosition !== endPosition
    ) {
      this.browser.setLocation(chromosome, startPosition, endPosition);
    }
    if (prevProps.thresholds !== thresholds) {
      this.updateTiers();
    }
  }

  updateTiers = () => {
    const { thresholds } = this.props;
    /* eslint-disable no-param-reassign */
    this.browser.tiers.forEach(tier => {
      tier.quantLeapThreshold = null;
      this.browser.refreshTier(tier);
    });
    Object.keys(thresholds).forEach(sourceName => {
      this.browser.tiers.forEach(tier => {
        if (tier.dasSource.name === sourceName) {
          tier.quantLeapThreshold = thresholds[sourceName];
          this.browser.refreshTier(tier);
        }
      });
    });
  };

  createPlot = () => {
    const {
      startPosition,
      endPosition,
      chromosome,
      onFeatureInfo,
      onTrackSelect,
      dispatch,
      onGeneSearch,
      genome,
      sources,
      gene,
    } = this.props;

    this.rangeStart = startPosition;
    this.rangeEnd = endPosition;

    // eslint-disable-next-line no-undef
    this.browser = new Browser({
      chr: chromosome,
      viewStart: startPosition,
      viewEnd: endPosition,
      cookieKey: `human-grc_${genome}`,
      noPersistView: true,
      fullScreen: false,
      noTitle: true,
      noHelp: true,
      rulerLocation: 'left',
      singleBaseHighlight: false,
      prefix: '/biodalliance/',
      maxHeight: 1000,
      noPersist: true,
      disablePoweredBy: true,
      coordSystem: GENOME_CONFIG[genome].coordSystem,
      onFirstRender: () => {
        if (gene) {
          this.browser.search(gene, err => {
            onGeneSearch(err === undefined);
            this.browser.clearHighlights();
          });
        }
      },
      sources: GENOME_CONFIG[genome].sources.concat(sources),
      chains: {
        hg19ToHg38: {
          uri: `${AWS_URL}/tracks/GRCh38/hg19ToHg38.bb`,
          type: 'bigbed',
          coords: COORD_HG19,
        },
      },
    });

    this.browser.addViewListener((chrNew, startNew, endNew) => {
      chrNew = `chr${chrNew}`;
      if (
        chrNew !== chromosome ||
        startNew !== startPosition ||
        endNew !== endPosition
      ) {
        if (CHROMOSOMES.includes(chrNew))
          dispatch(updateGenomicRange(chrNew, startNew, endNew));
      }
    });

    if (onFeatureInfo) this.browser.addFeatureInfoPlugin(onFeatureInfo);
    if (onTrackSelect)
      this.browser.addTierSelectionListener(idx => {
        onTrackSelect(this.browser.tiers[idx].dasSource.name);
      });
    this.browser.addInitListener(this.updateTiers);
  };

  render() {
    return (
      <div>
        <div id="svgHolder">Loading genome browser...</div>
      </div>
    );
  }
}

function select(state) {
  return {
    chromosome: state.genome.chromosome,
    startPosition: state.genome.startPosition,
    endPosition: state.genome.endPosition,
  };
}

export default connect(select)(GenomeBrowser);
